import { useMemo } from 'react';
import { useSearchParams } from '@remix-run/react';

import type { ElasticSearchViewProjectsByOptions } from '../type/api';
import { PAGE_KEY } from '~/modules/pagination/config/url-page-keys';
import { useMatchesHandleData } from '~/modules/root';
import type { ConstantLengthArray } from '~/utils/types';

export const VIEW_PROJECTS_BY_KEY = 'viewProjectsBy';
export const DEFAULT_VIEW_PROJECTS_BY_DISPLAY_NAME = 'All';

export const VIEW_PROJECTS_BY_OPTIONS: ConstantLengthArray<
    {
        item: string;
        param: ElasticSearchViewProjectsByOptions;
    },
    3
> = [
    {
        item: 'All',
        param: 'all',
    },
    {
        item: 'Our Projects',
        param: 'our-projects',
    },
    {
        item: "Projects We're Tagged In",
        param: 'projects-we-tagged-in',
    },
];

export interface ViewProjectsByOption {
    name: string;
    value: ElasticSearchViewProjectsByOptions;
    selected: boolean;
    header: string;
}

const useViewProjectsBy = (): {
    viewOptions: ViewProjectsByOption[];
    defaultOptionName: string | undefined;
} => {
    const showViewProjectsByFilters = useMatchesHandleData(
        'showViewProjectsByFilters',
        false
    );
    const [searchParams] = useSearchParams();

    const { viewOptions, defaultOptionName } = useMemo(() => {
        if (!showViewProjectsByFilters) {
            return { viewOptions: [], defaultOptionName: '' };
        }

        const availableOptions = VIEW_PROJECTS_BY_OPTIONS;

        const urlSortString = searchParams.get(VIEW_PROJECTS_BY_KEY);
        const urlSort = urlSortString
            ? (urlSortString as ElasticSearchViewProjectsByOptions)
            : null;

        let selectedOptionIndex = availableOptions.findIndex(
            (o) => o.param === urlSort
        );
        if (selectedOptionIndex === -1) selectedOptionIndex = 0;

        const defaultOptionName = availableOptions[0]?.item;

        const viewOptions = availableOptions.map((o, index) => {
            const copyParams = new URLSearchParams(searchParams);
            if (o.param) {
                copyParams.set(VIEW_PROJECTS_BY_KEY, o.param.toString());
            } else {
                copyParams.delete(VIEW_PROJECTS_BY_KEY);
            }
            copyParams.delete(PAGE_KEY);
            return {
                name: o.item,
                value: o.param,
                selected: selectedOptionIndex === index,
                header: index ? o.item : DEFAULT_VIEW_PROJECTS_BY_DISPLAY_NAME,
                state: { scroll: false },
            };
        });

        return {
            viewOptions,
            defaultOptionName,
        };
    }, [searchParams, showViewProjectsByFilters]);

    return {
        viewOptions,
        defaultOptionName,
    };
};

export { useViewProjectsBy };
